import React, { Fragment, useEffect, useState, useMemo } from "react";
import PageTitle from "../../../layouts/PageTitle";
import { useDispatch, useSelector } from "react-redux";
import SpinnerLoading from "../../../components/spinner-loading/SpinnerLoading";
import EmptyData from "../../../components/empty-data/EmptyData";
import { getSingleOrderFunc } from "../../../../store/custom-actions/orders-actions/GetSingleOrderSlicer";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import UpdateOrderModel from "../UpdateOrderModal";
import { getSiteSetting } from "../../../../utils/getSiteSetting";
import getImageUrl from "../../../../utils/getImageUrl";
import { formatCurrency } from "../../../../utils/formatCurrency";
import { usePrintInvoice } from "./hooks/usePrintInvoice";
import GatewayMetadataDisplay from "./components/GatewayMetadataDisplay";

/**
 * Invoice Page Component
 * Displays order details and provides printing functionality
 */
const InvoicePage = () => {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const dispatch = useDispatch();

  // Redux selectors
  const { loading, data: orderData } = useSelector((state) => state.getSingleOrderSlicer);
  const ordersSlicer = useSelector((state) => state.ordersSlicer);
  const { loading: switchLangLoading, data: languageData } = useSelector(
    (state) => state.getLanguageSettingsSlicer
  );

  // Local state
  const [order, setOrder] = useState({});
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [orderProducts, setOrderProducts] = useState([]);
  const [totalRefudProductsPrice, setTotalRefudProductsPrice] = useState(0);
  const [showUpdateModel, setShowUpdateModel] = useState(false);
  const [expandedPayments, setExpandedPayments] = useState({});

  // Custom hooks
  const { isPrinting, handlePrint: printInvoice, handlePreview: previewInvoice } = usePrintInvoice();

  // Fetch order data on mount
  useEffect(() => {
    if (orderId) {
      dispatch(getSingleOrderFunc(orderId));
    }
  }, [dispatch, orderId]);

  // Process order data
  useEffect(() => {
    if (orderData) {
      setOrder(orderData);
      const date = new Date(orderData.created_at);
      setYear(date.getFullYear());
      setMonth(date.getMonth() + 1);
      setDay(date.getDate());
      setOrderProducts(orderData.products || []);
    }
  }, [orderData]);

  // Calculate total refund price
  useEffect(() => {
    if (orderProducts && orderProducts.length > 0) {
      const allRefundPrice = orderProducts
        .map((product) => (product.pivot?.refund_qty || 0) * (product.pivot?.price || 0))
        .reduce((acc, current) => acc + current, 0);
      setTotalRefudProductsPrice(allRefundPrice);
    }
  }, [orderProducts]);

  // Helper functions
  const switchOrderStatus = (status) => {
    switch (status) {
      case 0:
        return t("Pending");
      case 1:
        return t("Active");
      case 2:
        return t("Delivery");
      case 3:
        return t("Completed");
      default:
        return t("Pending");
    }
  };

  const handleShowTitle = (translationsArray) => {
    if (translationsArray?.length > 1) {
      const titleObj = translationsArray?.find(
        (item) => item?.locale === languageData?.defaultLang?.slug
      );
      return titleObj?.title;
    } else {
      const titleObj = translationsArray[0];
      return titleObj?.title;
    }
  };

  // Memoize invoice data
  const invoiceData = useMemo(
    () => ({
      siteTitle: getSiteSetting("site_title"),
      invoiceLogoUrl: getImageUrl(getSiteSetting("invoice_logo")?.path),
      companyEmail:
        getSiteSetting("invoice_email") ||
        getSiteSetting("site_email") ||
        "info@company.com",
      companyPhone:
        getSiteSetting("invoice_phone") ||
        getSiteSetting("site_phone") ||
        "+1 234 567 8900",
      formattedDate: [year, month, day].filter(Boolean).join(" / "),
      hasOrder: orderData && Object.keys(order).length >= 1,
      isRTL: languageData?.defaultLang?.is_rtl || false,
      lang: languageData?.defaultLang?.slug || "ar",
    }),
    [year, month, day, orderData, order, languageData]
  );

  const {
    siteTitle,
    invoiceLogoUrl,
    companyEmail,
    companyPhone,
    formattedDate,
    hasOrder,
    isRTL,
    lang,
  } = invoiceData;

  // Prepare invoice data for printing
  const prepareInvoiceData = () => ({
    orderData,
    order,
    orderProducts,
    totalRefudProductsPrice,
    siteTitle,
    invoiceLogoUrl,
    companyEmail,
    companyPhone,
    formattedDate,
    isRTL,
    lang,
    switchOrderStatus,
    handleShowTitle,
    switchLangLoading,
  });

  // Print handlers
  const handlePrint = () => {
    if (!hasOrder) return;
    printInvoice(prepareInvoiceData());
  };

  const handlePreview = () => {
    if (!hasOrder) return;
    previewInvoice(prepareInvoiceData());
  };

  return (
    <Fragment>
      <PageTitle activeMenu="Invoice" motherMenu="Orders" />
      
      {/* Action Buttons */}
      <div className="d-flex flex-wrap gap-2 mt-2 mb-2">
        <Button
          variant="primary"
          size="sm"
          onClick={handlePrint}
          disabled={!hasOrder || isPrinting}
        >
          {isPrinting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              {t("Printing...")}
            </>
          ) : (
            <>
              <i className="fa-solid fa-print me-1"></i>
              {t("Print Invoice")}
            </>
          )}
        </Button>

        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handlePreview}
          disabled={!hasOrder || isPrinting}
        >
          <i className="fa-solid fa-eye me-1"></i>
          {t("Preview")}
        </Button>

        {order?.id && (
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowUpdateModel(true)}
          >
            <i className="fa-solid fa-pen-to-square me-1"></i>
            {t("Update Order")}
          </Button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <SpinnerLoading />
        </div>
      ) : hasOrder ? (
        <div className="invoice-container">
          {/* Invoice Header */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                          <i className="fas fa-file-invoice fa-2x text-muted"></i>
                        </div>
                        <div>
                          <h4 className="mb-1 fw-bold text-dark">
                            {t("Invoice")} #{orderData?.invoice_no || orderData?.id}
                          </h4>
                          <p className="mb-0 text-muted">
                            <i className="fas fa-calendar-alt me-1"></i>
                            {formattedDate}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4 text-md-end">
                      <div className="d-flex flex-column gap-2">
                        <div className="d-flex align-items-center justify-content-md-end gap-2">
                          <span className="fw-semibold text-dark">
                            {t("Order Status")}:
                          </span>
                          <span
                            className={`badge fs-6 px-3 py-2 ${
                              order.status === 3
                                ? "bg-success text-white"
                                : order.status === 2
                                ? "bg-info text-white"
                                : order.status === 1
                                ? "bg-warning text-dark"
                                : "bg-secondary text-white"
                            }`}
                          >
                            {switchOrderStatus(order.status)}
                          </span>
                        </div>
                        <div className="d-flex align-items-center justify-content-md-end gap-2">
                          <span className="fw-semibold text-dark">
                            {t("Payment")}:
                          </span>
                          <span
                            className={`badge fs-6 px-3 py-2 ${
                              orderData?.payment_status === "complete"
                                ? "bg-success text-white"
                                : orderData?.payment_status === "pending"
                                ? "bg-warning text-dark"
                                : "bg-danger text-white"
                            }`}
                          >
                            {orderData?.payment_status || t("Unknown")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body p-4">
                  {/* Company and Customer Information */}
                  <div className="row mb-4">
                    <div className="col-lg-4 col-md-6 mb-3">
                      <div className="border p-3">
                        <h6 className="text-dark mb-3 d-flex align-items-center">
                          <i className="fas fa-building me-2 text-muted"></i>
                          {t("From")}
                        </h6>
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-store text-muted me-2"></i>
                          <strong className="text-dark">{siteTitle}</strong>
                        </div>
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-envelope text-muted me-2"></i>
                          <span className="text-muted">{companyEmail}</span>
                        </div>
                        <div className="d-flex align-items-center">
                          <i className="fas fa-phone text-muted me-2"></i>
                          <span className="text-muted">{companyPhone}</span>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-4 col-md-6 mb-3">
                      <div className="border p-3">
                        <h6 className="text-dark mb-3 d-flex align-items-center">
                          <i className="fas fa-user me-2 text-muted"></i>
                          {t("To")}
                        </h6>
                        {orderData?.user ? (
                          <>
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-user-circle text-muted me-2"></i>
                              <strong className="text-dark">
                                {orderData?.user?.first_name} {orderData?.user?.last_name}
                              </strong>
                            </div>
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-envelope text-muted me-2"></i>
                              <span className="text-muted">{orderData?.user?.email}</span>
                            </div>
                            {orderData?.user?.phone && (
                              <div className="d-flex align-items-center">
                                <i className="fas fa-phone text-muted me-2"></i>
                                <span className="text-muted">{orderData?.user?.phone}</span>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-user-circle text-muted me-2"></i>
                              <strong className="text-dark">
                                {orderData?.guest_name || t("Guest Customer")}
                              </strong>
                            </div>
                            <div className="d-flex align-items-center mb-2">
                              <i className="fas fa-envelope text-muted me-2"></i>
                              <span className="text-muted">
                                {orderData?.guest_email || t("Not provided")}
                              </span>
                            </div>
                            {orderData?.phone && (
                              <div className="d-flex align-items-center">
                                <i className="fas fa-phone text-muted me-2"></i>
                                <span className="text-muted">{orderData?.phone}</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Shipping Address */}
                    <div className="col-lg-4 col-md-12 mb-3">
                      <div className="border p-3">
                        <h6 className="text-dark mb-3 d-flex align-items-center">
                          <i className="fas fa-shipping-fast me-2 text-muted"></i>
                          {t("Shipping Address")}
                        </h6>
                        {orderData?.shipping_address ? (
                          <div className="small">
                            {orderData.shipping_address.address && (
                              <div className="d-flex align-items-start mb-1">
                                <i className="fas fa-map-marker-alt text-muted me-2 mt-1"></i>
                                <span className="text-muted">
                                  {orderData.shipping_address.address}
                                </span>
                              </div>
                            )}
                            {orderData.shipping_address.city && (
                              <div className="d-flex align-items-center mb-1">
                                <i className="fas fa-city text-muted me-2"></i>
                                <span className="text-muted">
                                  {orderData.shipping_address.city}
                                </span>
                              </div>
                            )}
                            {orderData.shipping_address.state && (
                              <div className="d-flex align-items-center mb-1">
                                <i className="fas fa-map text-muted me-2"></i>
                                <span className="text-muted">
                                  {orderData.shipping_address.state}
                                </span>
                              </div>
                            )}
                            {orderData.shipping_address.country && (
                              <div className="d-flex align-items-center">
                                <i className="fas fa-flag text-muted me-2"></i>
                                <span className="text-muted">
                                  {orderData.shipping_address.country}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-muted">
                            <i className="fas fa-exclamation-circle me-1"></i>
                            {t("No shipping address provided")}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Logo Section */}
                  <div className="row">
                    <div className="col-12 text-center">
                      <div className="card border-0 bg-transparent">
                        <div className="card-body">
                          <div className="brand-logo">
                            <img
                              src={invoiceLogoUrl}
                              alt="invoice logo"
                              className="img-fluid"
                              style={{ maxWidth: "200px", height: "auto" }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Products Table */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="border">
                        <div className="bg-light p-3 border-bottom">
                          <h6 className="mb-0 text-dark d-flex align-items-center">
                            <i className="fas fa-shopping-cart me-2 text-muted"></i>
                            {t("Order Items")}
                          </h6>
                        </div>
                        <div className="p-0">
                          <div className="table-responsive">
                            <table className="table table-bordered mb-0">
                              <thead className="bg-light">
                                <tr>
                                  <th className="text-center fw-semibold text-dark">#</th>
                                  <th className="fw-semibold text-dark">{t("Item")}</th>
                                  <th className="fw-semibold text-dark">{t("Variant")}</th>
                                  <th className="text-center fw-semibold text-dark">
                                    {t("Quantity")}
                                  </th>
                                  <th className="text-end fw-semibold text-dark">
                                    {t("Price")}
                                  </th>
                                  <th className="text-center fw-semibold text-dark">
                                    {t("Refund")}
                                  </th>
                                  <th className="text-center fw-semibold text-dark">
                                    {t("SKU")}
                                  </th>
                                  <th className="text-end fw-semibold text-dark">
                                    {t("Total")}
                                  </th>
                                  <th className="text-end fw-semibold text-dark">
                                    {t("Refund Total")}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {orderProducts &&
                                  orderProducts.length >= 1 &&
                                  orderProducts.map((product, index) => {
                                    const { translations: mainTranslations } = product;
                                    const variantId = product?.pivot?.product_variant_id;
                                    const matchedVariant = product?.variants.find(
                                      (variant) => variant.id == variantId
                                    );

                                    return (
                                      <tr key={index} className="align-middle">
                                        <td className="text-center">
                                          <span className="badge bg-secondary text-white">
                                            {product.id}
                                          </span>
                                        </td>
                                        <td>
                                          <div>
                                            <strong className="text-dark">
                                              {switchLangLoading
                                                ? `${t("Loading")}...`
                                                : handleShowTitle(mainTranslations)}
                                            </strong>
                                            <br />
                                            <small className="text-muted">{product.sku}</small>
                                          </div>
                                        </td>

                                        {/* Variant Column */}
                                        <td>
                                          {matchedVariant ? (
                                            <div>
                                              <div className="mb-1">
                                                <small className="text-muted">
                                                  {t("SKU")}: {matchedVariant.sku}
                                                </small>
                                              </div>
                                              {matchedVariant.attribute_values &&
                                                matchedVariant.attribute_values.length > 0 && (
                                                  <div className="d-flex flex-wrap gap-1">
                                                    {matchedVariant.attribute_values.map(
                                                      (attr, attrIndex) => (
                                                        <div
                                                          key={attr.id}
                                                          className="d-flex align-items-center"
                                                        >
                                                          {attr.hex ? (
                                                            <div className="d-flex align-items-center">
                                                              <span
                                                                className="rounded-circle me-1"
                                                                style={{
                                                                  width: "12px",
                                                                  height: "12px",
                                                                  backgroundColor: attr.hex,
                                                                  border: "1px solid #ccc",
                                                                }}
                                                              ></span>
                                                              <small className="text-muted">
                                                                {attr.value}
                                                              </small>
                                                            </div>
                                                          ) : (
                                                            <span className="badge bg-light text-dark border">
                                                              {attr.value}
                                                            </span>
                                                          )}
                                                          {attrIndex <
                                                            matchedVariant.attribute_values
                                                              .length -
                                                              1 && (
                                                            <span className="mx-1 text-muted">
                                                              |
                                                            </span>
                                                          )}
                                                        </div>
                                                      )
                                                    )}
                                                  </div>
                                                )}
                                            </div>
                                          ) : (
                                            <span className="text-muted">{t("No Variant")}</span>
                                          )}
                                        </td>

                                        <td className="text-center">
                                          <span className="badge bg-secondary text-white">
                                            {product.pivot?.qty || 0}
                                          </span>
                                        </td>
                                        <td className="text-end">
                                          <strong className="text-dark">
                                            {formatCurrency(product.pivot?.price || 0)}
                                          </strong>
                                        </td>
                                        <td className="text-center">
                                          {parseInt(product.pivot?.refund_qty || 0) >= 1 ? (
                                            <span className="badge bg-warning text-dark">
                                              -{product.pivot.refund_qty}
                                            </span>
                                          ) : (
                                            <span className="text-muted">{t("No Refund")}</span>
                                          )}
                                        </td>
                                        <td className="text-center">
                                          <small className="text-muted">
                                            {product.sku || t("N/A")}
                                          </small>
                                        </td>
                                        <td className="text-end">
                                          <strong className="text-dark">
                                            {formatCurrency(
                                              parseInt(product.pivot?.qty || 0) *
                                                parseInt(product.pivot?.price || 0)
                                            )}
                                          </strong>
                                        </td>
                                        <td className="text-end">
                                          {parseInt(product.pivot?.refund_qty || 0) >= 1 ? (
                                            <strong className="text-danger">
                                              -
                                              {formatCurrency(
                                                parseInt(product.pivot.refund_qty) *
                                                  parseInt(product.pivot.price)
                                              )}
                                            </strong>
                                          ) : (
                                            <span className="text-muted">
                                              {formatCurrency(0)}
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="row">
                    <div className="col-lg-6"></div>
                    <div className="col-lg-6">
                      <div className="border">
                        <div className="bg-light p-3 border-bottom">
                          <h6 className="mb-0 text-dark d-flex align-items-center">
                            <i className="fas fa-calculator me-2 text-muted"></i>
                            {t("Order Summary")}
                          </h6>
                        </div>
                        <div className="p-0">
                          <div className="table-responsive">
                            <table className="table table-borderless mb-0">
                              <tbody>
                                <tr className="border-bottom">
                                  <td className="py-3">
                                    <strong className="text-dark">{t("Subtotal")}</strong>
                                  </td>
                                  <td className="text-end py-3">
                                    <strong className="text-dark">
                                      {formatCurrency(parseFloat(order.total_price || 0))}
                                    </strong>
                                  </td>
                                </tr>
                                <tr className="border-bottom">
                                  <td className="py-2">
                                    <span className="text-muted">{t("Discount")}</span>
                                  </td>
                                  <td className="text-end py-2">
                                    <span className="text-muted">
                                      -{formatCurrency(parseFloat(order.discount) || 0)}
                                    </span>
                                  </td>
                                </tr>
                                <tr className="border-bottom">
                                  <td className="py-2">
                                    <span className="text-muted">{t("Shipping")}</span>
                                  </td>
                                  <td className="text-end py-2">
                                    {parseFloat(order.shipping_amount || 0) >= 1 ? (
                                      <span className="text-dark">
                                        {formatCurrency(parseFloat(order.shipping_amount))}
                                      </span>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                </tr>
                                <tr className="border-bottom">
                                  <td className="py-2">
                                    <span className="text-muted">{t("TAX")}</span>
                                  </td>
                                  <td className="text-end py-2">
                                    <span className="text-dark">
                                      {formatCurrency(parseFloat(order.tax_fee_amount || 0))}
                                    </span>
                                  </td>
                                </tr>
                                {parseFloat(totalRefudProductsPrice) > 0 && (
                                  <tr className="border-bottom">
                                    <td className="py-2">
                                      <span className="text-muted">{t("Total Refund")}</span>
                                    </td>
                                    <td className="text-end py-2">
                                      <span className="text-danger fw-bold">
                                        -{formatCurrency(parseFloat(totalRefudProductsPrice))}
                                      </span>
                                    </td>
                                  </tr>
                                )}
                                <tr className="bg-light">
                                  <td className="py-3">
                                    <h6 className="mb-0 text-dark">{t("Total")}</h6>
                                  </td>
                                  <td className="text-end py-3">
                                    <h5 className="mb-0 text-dark fw-bold">
                                      {formatCurrency(parseFloat(order.payable_amount || 0))}
                                    </h5>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-footer">
                  {/* Payment Information */}
                  {orderData?.payments && orderData.payments.length > 0 && (
                    <div className="row mt-4">
                      <div className="col-12">
                        <div className="border">
                          <div className="p-3 border-bottom">
                            <h6 className="mb-0 text-dark d-flex align-items-center">
                              <i className="fas fa-credit-card me-2 text-muted"></i>
                              {t("Payment Information")}
                            </h6>
                          </div>
                          <div className="p-0">
                            <div className="table-responsive">
                              <table className="table table-bordered mb-0">
                                <thead className="bg-light">
                                  <tr>
                                    <th className="fw-semibold text-dark">
                                      {t("Payment Gateway")}
                                    </th>
                                    <th className="fw-semibold text-dark">
                                      {t("Transaction ID")}
                                    </th>
                                    <th className="text-end fw-semibold text-dark">
                                      {t("Amount")}
                                    </th>
                                    <th className="text-center fw-semibold text-dark">
                                      {t("Currency")}
                                    </th>
                                    <th className="text-center fw-semibold text-dark">
                                      {t("Status")}
                                    </th>
                                    <th className="text-center fw-semibold text-dark">
                                      {t("Date")}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {orderData.payments.map((payment, index) => (
                                    <React.Fragment key={index}>
                                      <tr className="align-middle">
                                        <td>
                                          <span className="badge bg-secondary text-white">
                                            {payment.payment_gateway || t("Unknown")}
                                          </span>
                                        </td>
                                        <td>
                                          <code className="text-dark">
                                            {payment.gateway_transaction_id || t("N/A")}
                                          </code>
                                        </td>
                                        <td className="text-end">
                                          <strong className="text-dark">
                                            {formatCurrency(parseFloat(payment.amount || 0))}
                                          </strong>
                                        </td>
                                        <td className="text-center">
                                          <span className="badge bg-light text-dark border">
                                            {payment.currency || t("N/A")}
                                          </span>
                                        </td>
                                        <td className="text-center">
                                          <span
                                            className={`badge ${
                                              payment.status === "completed"
                                                ? "bg-success text-white"
                                                : payment.status === "pending"
                                                ? "bg-warning text-dark"
                                                : "bg-danger text-white"
                                            }`}
                                          >
                                            {payment.status || t("Unknown")}
                                          </span>
                                        </td>
                                        <td className="text-center">
                                          <small className="text-muted">
                                            {payment.created_at
                                              ? new Date(payment.created_at).toLocaleDateString()
                                              : t("N/A")}
                                          </small>
                                        </td>
                                      </tr>

                                      {/* Failure Reason */}
                                      {payment.failure_reason && (
                                        <tr>
                                          <td colSpan="6" className="p-0">
                                            <div
                                              className="alert alert-danger m-0 border-0 rounded-0 d-flex align-items-start"
                                              role="alert"
                                              style={{
                                                backgroundColor: "#fff5f5",
                                                borderLeft: "4px solid #dc3545",
                                                padding: "1rem 1.25rem",
                                              }}
                                            >
                                              <div className="flex-shrink-0 me-3">
                                                <i
                                                  className="fas fa-exclamation-triangle"
                                                  style={{
                                                    fontSize: "1.25rem",
                                                    color: "#dc3545",
                                                  }}
                                                ></i>
                                              </div>
                                              <div className="flex-grow-1">
                                                <strong
                                                  className="d-block mb-2"
                                                  style={{ fontSize: "0.95rem" }}
                                                >
                                                  {t("Failure Reason")}
                                                </strong>
                                                <p
                                                  className="mb-0 text-dark"
                                                  style={{
                                                    fontSize: "0.9rem",
                                                    lineHeight: "1.5",
                                                  }}
                                                >
                                                  {payment.failure_reason}
                                                </p>
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      )}

                                      {/* Gateway Metadata Details */}
                                      {payment.gateway_metadata && (
                                        <tr>
                                          <td colSpan="6" className="p-0">
                                            <div className="border-top">
                                              <button
                                                className="w-100 btn btn-light text-start p-3 d-flex align-items-center justify-content-between"
                                                type="button"
                                                onClick={() => {
                                                  setExpandedPayments((prev) => ({
                                                    ...prev,
                                                    [index]: !prev[index],
                                                  }));
                                                }}
                                                style={{
                                                  border: "none",
                                                  borderBottom: "1px solid #dee2e6",
                                                }}
                                              >
                                                <span>
                                                  <i className="fas fa-info-circle me-2"></i>
                                                  {t("Gateway Metadata Details")}
                                                </span>
                                                <i
                                                  className={`fas fa-chevron-${
                                                    expandedPayments[index] ? "up" : "down"
                                                  }`}
                                                ></i>
                                              </button>
                                              {expandedPayments[index] && (
                                                <div className="p-3 bg-white">
                                                  <GatewayMetadataDisplay
                                                    metadata={payment.gateway_metadata}
                                                  />
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <EmptyData />
      )}

      {/* Update Order Modal */}
      <UpdateOrderModel
        show={showUpdateModel}
        onHide={() => setShowUpdateModel(false)}
        order={order}
        submitLoading={ordersSlicer?.loading?.update}
        onUpdateSuccess={(updatedOrder) => {
          if (orderId) {
            dispatch(getSingleOrderFunc(orderId));
          }
        }}
      />
    </Fragment>
  );
};

export default InvoicePage;

